﻿import { useEffect, useState } from 'react';
import {
  Page, Card, Button, Banner, Badge, Text, Select,
  DataTable, EmptyState, Spinner, Layout, BlockStack,
  InlineStack, Box, Pagination, InlineGrid,
} from '@shopify/polaris';
import { PriceListIcon, ChartHistogramGrowthIcon, ChartHistogramFullIcon } from '@shopify/polaris-icons';
import { usePriceOptimization } from '../../hooks/usePriceOptimization';
import type { PriceOptimizationInsight, PriceRecommendationType } from '../../api/priceOptimization';
import { StatCard } from '../shared/StatCard';
import { IconBadge } from '../shared/IconBadge';

function typeBadge(type: PriceRecommendationType) {
  return type === 'price_increase'
    ? <Badge tone="success">Price increase</Badge>
    : <Badge tone="attention">Clearance discount</Badge>;
}

interface DetailProps {
  insight: PriceOptimizationInsight;
  onBack: () => void;
  onDelete: (id: number) => void;
}

function InsightDetail({ insight, onBack, onDelete }: DetailProps) {
  return (
    <Page fullWidth
      title={`Price Optimization: ${insight.period_start} – ${insight.period_end}`}
      backAction={{ content: 'All Insights', onAction: onBack }}
      secondaryActions={[{
        content: 'Delete',
        destructive: true,
        onAction: () => { onDelete(insight.id); onBack(); },
      }]}
    >
      <Layout>
        <Layout.Section>
          <BlockStack gap="400">
            <Card>
              <BlockStack gap="100">
                <Text as="p" variant="bodySm" tone="subdued">Orders analysed</Text>
                <Text as="p" variant="heading2xl" fontWeight="bold">{insight.orders_analyzed}</Text>
              </BlockStack>
            </Card>

            {insight.ai_summary && (
              <Card>
                <BlockStack gap="200">
                  <Text as="h2" variant="headingMd">Summary</Text>
                  <Text as="p" variant="bodyMd">{insight.ai_summary}</Text>
                </BlockStack>
              </Card>
            )}

            <Card>
              <BlockStack gap="400">
                <Text as="h2" variant="headingMd">Recommendations</Text>
                {insight.ai_recommendations.length === 0 ? (
                  <Text as="p" tone="subdued">No recommendations generated for this period.</Text>
                ) : (
                  <BlockStack gap="300">
                    {insight.ai_recommendations.map((r, i) => (
                      <Card key={i} background="bg-surface-secondary">
                        <BlockStack gap="150">
                          <InlineStack gap="300" blockAlign="center" wrap={false}>
                            <IconBadge icon={PriceListIcon} color={r.type === 'price_increase' ? 'green' : 'orange'} />
                            <BlockStack gap="050">
                              <Text as="h3" variant="headingSm">{r.product_title}</Text>
                              {typeBadge(r.type)}
                            </BlockStack>
                          </InlineStack>
                          <Text as="p" variant="bodyMd">{r.rationale}</Text>
                          <Text as="p" variant="bodySm" tone="success">
                            Expected impact: {r.expected_impact}
                          </Text>
                          {r.suggested_price != null && (
                            <InlineStack gap="200" blockAlign="center">
                              <Text as="span" variant="bodySm" tone="subdued">
                                <s>${r.current_price.toFixed(2)}</s>
                              </Text>
                              <Text as="span" variant="bodySm" fontWeight="semibold">
                                ${r.suggested_price.toFixed(2)}
                              </Text>
                              <Badge tone={r.type === 'price_increase' ? 'success' : 'attention'}>
                                {`${r.type === 'price_increase' ? '+' : '-'}${r.suggested_price_change_percent}%`}
                              </Badge>
                            </InlineStack>
                          )}
                        </BlockStack>
                      </Card>
                    ))}
                  </BlockStack>
                )}
              </BlockStack>
            </Card>

            <Card>
              <BlockStack gap="400">
                <Text as="h2" variant="headingMd">Bestsellers</Text>
                {insight.bestsellers.length === 0 ? (
                  <Text as="p" tone="subdued">No bestseller candidates found for this period.</Text>
                ) : (
                  <DataTable
                    columnContentTypes={['text', 'numeric', 'numeric']}
                    headings={['Product', 'Price', 'Units Sold']}
                    rows={insight.bestsellers.map((p) => [p.title, `$${p.price.toFixed(2)}`, String(p.units_sold)])}
                  />
                )}
              </BlockStack>
            </Card>

            <Card>
              <BlockStack gap="400">
                <Text as="h2" variant="headingMd">Slow Movers</Text>
                {insight.slow_movers.length === 0 ? (
                  <Text as="p" tone="subdued">No slow movers found for this period.</Text>
                ) : (
                  <DataTable
                    columnContentTypes={['text', 'numeric', 'numeric']}
                    headings={['Product', 'Price', 'Units In Stock']}
                    rows={insight.slow_movers.map((p) => [p.title, `$${p.price.toFixed(2)}`, String(p.inventory)])}
                  />
                )}
              </BlockStack>
            </Card>
          </BlockStack>
        </Layout.Section>
      </Layout>
    </Page>
  );
}

const PERIOD_OPTIONS = [
  { label: 'Last 30 days', value: '30' },
  { label: 'Last 60 days', value: '60' },
  { label: 'Last 90 days', value: '90' },
];

export function PriceOptimizationPanel() {
  const {
    insights, activeInsight, loading, generating, error,
    total, page, lastPage, fetchInsights, generate, deleteInsight, setActiveInsight, setError,
  } = usePriceOptimization();

  const [days, setDays] = useState('60');

  useEffect(() => {
    fetchInsights(1);
  }, []);

  if (activeInsight) {
    return (
      <InsightDetail
        insight={activeInsight}
        onBack={() => setActiveInsight(null)}
        onDelete={deleteInsight}
      />
    );
  }

  const latest = insights[0];

  return (
    <Page fullWidth
      title="Price Optimization Insights"
      subtitle="Finds bestsellers that could bear a price increase and slow movers worth discounting to clear inventory"
    >
      <Layout>
        <Layout.Section>
          <BlockStack gap="400">
          {error && (
            <Banner tone="critical" onDismiss={() => setError(null)}>
              <p>{error}</p>
            </Banner>
          )}

          <InlineGrid columns={{ xs: 1, sm: 3 }} gap="300">
            <StatCard
              label="Price Opportunities (latest run)"
              value={String(latest?.ai_recommendations.length ?? 0)}
              icon={PriceListIcon}
              iconColor="purple"
            />
            <StatCard
              label="Bestsellers Found"
              value={String(latest?.bestsellers.length ?? 0)}
              icon={ChartHistogramGrowthIcon}
              iconColor="green"
              deltaLabel="Candidates for a price increase"
            />
            <StatCard
              label="Slow Movers Found"
              value={String(latest?.slow_movers.length ?? 0)}
              icon={ChartHistogramFullIcon}
              iconColor="orange"
              deltaLabel="Candidates for a clearance discount"
            />
          </InlineGrid>

          <Card>
            <BlockStack gap="400">
              <Text as="h2" variant="headingMd">Generate New Insights</Text>
              <Text as="p" variant="bodySm" tone="subdued">
                Analyses real sales velocity against your product catalog to flag pricing opportunities.
              </Text>
              <InlineStack gap="300" blockAlign="end">
                <Box minWidth="200px">
                  <Select label="Period" options={PERIOD_OPTIONS} value={days} onChange={setDays} />
                </Box>
                <Button
                  variant="primary"
                  loading={generating}
                  onClick={() => generate({ days: Number(days) })}
                >
                  Generate Insights
                </Button>
              </InlineStack>
            </BlockStack>
          </Card>

          <Card>
            <BlockStack gap="400">
              <Text as="h2" variant="headingMd">Past Insights ({total})</Text>

              {loading ? (
                <InlineStack align="center"><Spinner /></InlineStack>
              ) : insights.length === 0 ? (
                <EmptyState
                  heading="No price optimization insights yet"
                  image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
                  action={{ content: 'Generate First Insights', onAction: () => generate({ days: Number(days) }) }}
                >
                  <p>Generate insights to find price increase and clearance opportunities from your sales history.</p>
                </EmptyState>
              ) : (
                <DataTable
                  columnContentTypes={['text', 'numeric', 'numeric', 'text']}
                  headings={['Period', 'Orders Analysed', 'Recommendations', 'Actions']}
                  rows={insights.map((i) => [
                    <Button variant="plain" onClick={() => setActiveInsight(i)}>{`${i.period_start} – ${i.period_end}`}</Button>,
                    String(i.orders_analyzed),
                    String(i.ai_recommendations.length),
                    <Button variant="plain" tone="critical" size="slim" onClick={() => deleteInsight(i.id)}>Delete</Button>,
                  ])}
                />
              )}

              {lastPage > 1 && (
                <InlineStack align="center">
                  <Pagination
                    hasPrevious={page > 1}
                    onPrevious={() => fetchInsights(page - 1)}
                    hasNext={page < lastPage}
                    onNext={() => fetchInsights(page + 1)}
                  />
                </InlineStack>
              )}
            </BlockStack>
          </Card>
          </BlockStack>
        </Layout.Section>
      </Layout>
    </Page>
  );
}
